import axios from 'Src/helpers/api/axiosInstance';
import { AppUser } from 'Src/views/App/App';
import { DEFAULT_FILTER } from './user';
import { Count } from '../../../models/count';
import { SchoolStudent } from '../../../models/schoolStudent';
import { fetchAllSearchPages } from './fetchAllSearchPages';

interface StudentData {
  firstName: string;
  lastName: string;
  gender: string;
  birthday?: string;
  formId?: string;
  houseId?: string;
  medicalInfo?: string;
  nextOfKin?: any[];
  misId?: string;
}

/** STUDENTS TABLE */
export function getSchoolStudents(user: AppUser, filter = DEFAULT_FILTER) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key },
    params: {
      filter
    }
  };

  return axios.get(`${window.apiBase}/i/schools/${activeSchoolId}/students`, config).then(response => {
    return response.data;
  });
}

export function searchSchoolStudents(user: AppUser, filter): Promise<{ items: SchoolStudent[]; total: number }> {
  const { roleSessionKey: key, activeSchoolId } = user;
  return axios
    .post(`${window.apiBase}/i/schools/${activeSchoolId}/studentsList/search`, filter, { headers: { usid: key } })
    .then(response => response.data);
}

export function getAllSchoolStudents(user: AppUser, where?) {
  return fetchAllSearchPages(filter => searchSchoolStudents(user, filter), where || {});
}

export function getSchoolStudentsCount(user: AppUser, where?): Promise<Count> {
  const { roleSessionKey: key, activeSchoolId } = user;

  let config;

  if (typeof where !== 'undefined') {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: where
        }
      }
    };
  } else {
    config = {
      headers: { usid: key }
    };
  }

  return axios.get<Count>(`${window.apiBase}/i/schools/${activeSchoolId}/students/count`, config).then(response => {
    return response.data;
  });
}

export function addSchoolStudent(user: AppUser, data) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key }
  };

  return axios.post(`${window.apiBase}/i/schools/${activeSchoolId}/students`, data, config).then(response => {
    return response.data;
  });
}

export function deleteStudent(user: AppUser, studentId) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key }
  };

  return axios.delete(`${window.apiBase}/i/schools/${activeSchoolId}/students/${studentId}`, config).then(response => {
    return response.data;
  });
}

export function removeStudentsInBulk(user: AppUser, studentIds: string[]) {
  const { roleSessionKey: key, activeSchoolId } = user;
  return axios
    .post(
      `${window.apiBase}/i/schools/${activeSchoolId}/students/bulkRemove`,
      { studentIds },
      { headers: { usid: key } }
    )
    .then(response => response.data);
}

export function updateSchoolStudent(user: AppUser, studentId, data) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key }
  };

  return axios
    .put(`${window.apiBase}/i/schools/${activeSchoolId}/students/${studentId}`, data, config)
    .then(response => {
      return response.data;
    });
}

export interface StudentAgeGroupCheckStudent {
  id: string;
  firstName: string;
  lastName: string;
  birthday: string;
  currentFormId?: string;
  currentFormName?: string;
  currentAge?: number;
  currentAgeGroup?: string;
  expectedAge: number;
  expectedAgeGroup: string;
}

export interface StudentAgeGroupCheckPreview {
  academicYearStart: number;
  ageGroupsNaming: string;
  ageGroups: string[];
  forms: Array<{ id: string; name: string; age: number }>;
  counts: { total: number; withBirthday: number; withoutBirthday: number; mismatched: number };
  students: StudentAgeGroupCheckStudent[];
}

export function getStudentAgeGroupCheck(user: AppUser): Promise<StudentAgeGroupCheckPreview> {
  const { roleSessionKey: key, activeSchoolId } = user;
  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/student-age-group-check`, { headers: { usid: key } })
    .then(response => response.data);
}

export function applyStudentAgeGroupCheck(
  user: AppUser,
  assignments: Array<{ studentId: string; action: 'KEEP' | 'MOVE' | 'REMOVE'; formId?: string; targetAge?: number }>
): Promise<{ moved: number; removed: number; kept: number; createdForms: number }> {
  const { roleSessionKey: key, activeSchoolId } = user;
  return axios
    .post(
      `${window.apiBase}/i/schools/${activeSchoolId}/student-age-group-check/apply`,
      { assignments },
      { headers: { usid: key } }
    )
    .then(response => response.data);
}

export function assignUserTagByStudentId(user: AppUser, data, studentId) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key }
  };

  return axios
    .post(`${window.apiBase}/i/schools/${activeSchoolId}/students/${studentId}/userTags`, data, config)
    .then(response => {
      return response.data;
    });
}

export function deleteUserTagByStudentId(user: AppUser, studentId, userTagId) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key }
  };

  return axios
    .delete(`${window.apiBase}/i/schools/${activeSchoolId}/students/${studentId}/userTags/${userTagId}`, config)
    .then(response => {
      return response.data;
    });
}

export function updateStudentTagsInBulk(
  user: AppUser,
  userIds: string[],
  userTagIds: string[],
  action: 'ADD' | 'REMOVE'
) {
  const { roleSessionKey: key, activeSchoolId } = user;
  return axios
    .post(
      `${window.apiBase}/i/schools/${activeSchoolId}/students/userTags/bulk`,
      { userIds, userTagIds, action },
      { headers: { usid: key } }
    )
    .then(response => response.data);
}

export function mergeStudent(user: AppUser, studentId, data: { permissionId: string; userId: string }) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key }
  };

  return axios
    .post(`${window.apiBase}/i/schools/${activeSchoolId}/students/${studentId}/merge`, data, config)
    .then(response => {
      return response.data;
    });
}

export async function uploadStudents(user: AppUser, students: StudentData[]): Promise<void> {
  for (const student of students) {
    try {
      await addSchoolStudent(user, student);
    } catch (error) {
      console.error(`Failed to upload student ${student.firstName} ${student.lastName}:`, error);
      throw error;
    }
  }
}

export function importStudents(user: AppUser, students: StudentData[], replaceExisting: boolean) {
  const { roleSessionKey: key, activeSchoolId } = user;
  return axios
    .post(
      `${window.apiBase}/i/schools/${activeSchoolId}/students/import`,
      { students, replaceExisting },
      { headers: { usid: key } }
    )
    .then(response => response.data);
}

export function bulkDeleteStudentsBySchoolId(user: AppUser) {
  const { roleSessionKey: key, activeSchoolId } = user;

  if (!activeSchoolId) {
    throw new Error('Active school ID is missing.');
  }

  const config = {
    headers: { usid: key }
  };

  return axios
    .delete(`${window.apiBase}/i/schools/${activeSchoolId}/students/bulkDelete`, config)
    .then(response => {
      return response.data;
    })
    .catch(error => {
      console.error('Error during bulk deletion:', error);
      throw error;
    });
}
